/**
 * O processamento automático da Pré-análise eletrônica (E89.F2, GUS-50).
 *
 * A tela só DISPARA e conta o que houve. Quem decide quais peças verificar e se
 * a primeira atualização das fontes externas é paga é o servidor (E89.F1,
 * ADR-0077 §2.6), e por isso a chamada não leva corpo.
 *
 * As três regras que moram aqui:
 *
 * 1. **Um disparo por chegada ao caso** — o padrão `autoRunAttempted` do portão
 *    da grafotécnica (E47.F1.T02). A marca é `ref`, e não estado: a aplicação
 *    roda em `StrictMode`, que executa o efeito duas vezes na montagem, e com
 *    estado as duas execuções leriam "ainda não disparou".
 * 2. **Falha não reexecuta sozinha** (RN06). Um erro de rede em laço gastaria
 *    chamada atrás de chamada; a nova tentativa é do usuário.
 * 3. **Documento adicionado rearma** (decisão do owner, 2026-09-12). Se houver
 *    ação de caso em voo, o pedido fica guardado e roda quando ela termina: não
 *    se perde e não corre em paralelo com outra ação.
 * 4. **Leitura por imagem em andamento espera e tenta de novo** (E101.F5.T01): o
 *    documento só em imagem é lido fora da requisição, e o servidor responde
 *    `leitura_em_andamento` até terminar. A tela avisa e pede de novo a cada
 *    `ESPERA_DA_LEITURA_MS`, até `TENTATIVAS_DA_LEITURA` vezes; a troca de caso
 *    cancela a espera. Não é a falha da regra 2: a chamada deu certo.
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ProtocoloFormAcao } from '@/components/ExameEletronico/ProtocoloForm';
import {
  processarInicial,
  type ProcessamentoInicialData,
} from '@/lib/exame_eletronico_processamento';

export interface UseProcessamentoInicialArgs {
  client: AxiosInstance;
  caseId: string;
  /** A primeira carga do caso chegou. Antes dela não há quadro a atualizar. */
  pronto: boolean;
  /**
   * A ação de caso em voo: o automático espera a vez, como qualquer outra.
   * Contrato com a página: trocar de caso zera a ação (F-E89.F2-01), porque o
   * desfecho do caso anterior que chega depois da troca não a solta.
   */
  acaoDoCaso: ProtocoloFormAcao | null;
  setAcaoDoCaso: (acao: ProtocoloFormAcao | null) => void;
  /** Relê o quadro gravado; rejeita quando a leitura falha. */
  recarregar: () => Promise<void>;
  /** A frase da tela para um erro, pela régua da página. */
  mensagemDoErro: (err: unknown) => string;
  /** A releitura depois do processamento falhou. */
  onErroDeLeitura: (mensagem: string) => void;
  /** O servidor verificou peça agora. Não é chamado no replay. */
  onExecutado: (resultado: ProcessamentoInicialData) => void;
}

export interface ProcessamentoInicialEstado {
  /** A frase da falha do último processamento automático, ou `null`. */
  falha: string | null;
  /** Pede um processamento: documento adicionado ou nova tentativa depois da falha. */
  solicitar: () => void;
  /** O servidor respondeu que a imagem de um documento ainda está sendo lida. */
  lendoImagem: boolean;
}

/** O intervalo entre os pedidos enquanto a imagem é lida (a leitura mede ~1,5 s por página). */
export const ESPERA_DA_LEITURA_MS = 5_000;
/** O teto de pedidos seguidos: 10 minutos, o dobro do dossiê de 60 páginas medido. */
export const TENTATIVAS_DA_LEITURA = 120;

type Desfecho = { ok: true; resultado: ProcessamentoInicialData } | { ok: false; erro: unknown };

/** A chamada, com a falha como valor — quem decide o que fazer com ela é o efeito. */
async function chamar(client: AxiosInstance, caseId: string): Promise<Desfecho> {
  try {
    return { ok: true, resultado: await processarInicial(client, caseId) };
  } catch (err) {
    return { ok: false, erro: err };
  }
}

export function useProcessamentoInicial(
  args: UseProcessamentoInicialArgs,
): ProcessamentoInicialEstado {
  const { client, caseId, pronto, acaoDoCaso, setAcaoDoCaso, recarregar } = args;
  // A falha é DO caso em que aconteceu: trocar de caso não a leva junto.
  const [falha, setFalha] = useState<{ caseId: string; mensagem: string } | null>(null);
  const [pedidos, setPedidos] = useState(0);
  const [lendoPara, setLendoPara] = useState<string | null>(null);
  const esperas = useRef({ caseId, n: 0 });
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disparadoPara = useRef<string | null>(null);
  const rearmado = useRef(false);
  const montado = useRef(true);
  const casoAtual = useRef(caseId);
  // Os callbacks da página mudam de identidade a cada render; lidos por ref, não
  // reagendam o efeito (lição do mock instável que virava laço).
  const avisos = useRef(args);
  avisos.current = args;

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  // A espera da leitura é do caso: trocar de caso (ou sair da tela) a cancela.
  useEffect(
    () => () => {
      if (espera.current !== null) clearTimeout(espera.current);
      espera.current = null;
    },
    [caseId],
  );

  // No render, e não num efeito: a continuação que resolve entre a troca e o
  // efeito ainda leria o caso anterior como vigente.
  casoAtual.current = caseId;

  useEffect(() => {
    const pendente = disparadoPara.current !== caseId || rearmado.current;
    if (!pronto || acaoDoCaso !== null || !pendente) return;
    disparadoPara.current = caseId;
    rearmado.current = false;
    setAcaoDoCaso('processando');
    setFalha(null);
    // O desfecho só vale enquanto a tela é a deste caso. A troca de caso zera a
    // ação de caso na página; o que chega tarde do anterior não relê, não solta
    // ação nenhuma e não anuncia nada (F-E89.F2-01).
    const vigente = (): boolean => montado.current && casoAtual.current === caseId;
    void (async () => {
      const desfecho = await chamar(client, caseId);
      if (!vigente()) return;
      // Relê SEMPRE. Depois de uma falha, porque numa queda de rede o servidor
      // pode ter concluído (E82.F5). Depois de um sucesso, mesmo calado, porque
      // "nada a verificar" não quer dizer "a tela está atual": outra chegada
      // pode ter gravado o quadro enquanto esta esperava a trava (E89.F2.C02).
      await recarregar().catch((err: unknown) => {
        if (vigente()) avisos.current.onErroDeLeitura(avisos.current.mensagemDoErro(err));
      });
      if (!vigente()) return;
      setAcaoDoCaso(null);
      if (!desfecho.ok)
        setFalha({ caseId, mensagem: avisos.current.mensagemDoErro(desfecho.erro) });
      else if (desfecho.resultado.executou) avisos.current.onExecutado(desfecho.resultado);
      esperarALeitura(desfecho.ok && desfecho.resultado.motivo === 'leitura_em_andamento');
    })();

    function esperarALeitura(lendo: boolean): void {
      if (esperas.current.caseId !== caseId) esperas.current = { caseId, n: 0 };
      setLendoPara(lendo ? caseId : null);
      if (!lendo) {
        esperas.current.n = 0;
        return;
      }
      if (esperas.current.n >= TENTATIVAS_DA_LEITURA) return;
      esperas.current.n += 1;
      espera.current = setTimeout(() => {
        espera.current = null;
        if (!vigente()) return;
        rearmado.current = true;
        setPedidos((n) => n + 1);
      }, ESPERA_DA_LEITURA_MS);
    }
  }, [client, caseId, pronto, acaoDoCaso, pedidos, setAcaoDoCaso, recarregar]);

  const solicitar = useCallback((): void => {
    rearmado.current = true;
    setPedidos((n) => n + 1);
  }, []);

  return {
    falha: falha?.caseId === caseId ? falha.mensagem : null,
    solicitar,
    lendoImagem: lendoPara === caseId,
  };
}
